import { 
  Heart, Sparkles, Palette, Camera, Eye, Scissors, Users, Brain, Scan, 
  Microscope, Zap, Star, Target, Store, Calendar, ShoppingBag, CreditCard, 
  Building2, Package, Truck, TrendingUp, Mail, MessageSquare, Users2, 
  Shield, FileText, Wrench, Activity, BarChart3, Database, Globe, Lock, 
  CheckCircle, Clock, Settings, Smartphone, Leaf, Banknote
} from 'lucide-react';
import { ProductConfigs } from './types';

export const completeHealthBeautySolutionsConfigs: ProductConfigs = {
  // All 29 Missing Health & Beauty Products with Complete 6+6+9 Structure

  // Business Management, POS & CRM Products
  'mindbody-beauty-management': {
    keyBenefits: [
      {
        icon: Store,
        title: 'All-in-One Beauty Business Suite',
        description: 'Comprehensive platform combining booking, POS, CRM, staff management, and marketing automation for complete beauty business operations.',
        metrics: '90% operational efficiency improvement',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Calendar,
        title: 'Advanced Appointment Management',
        description: 'Intelligent booking system with automated scheduling, staff optimization, and customer preference learning.',
        metrics: '85% reduction in scheduling conflicts',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Customer Relationship Excellence',
        description: 'Comprehensive CRM with customer history, preferences, treatment tracking, and personalized communication.',
        metrics: '200% improvement in customer retention',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: TrendingUp,
        title: 'Business Analytics Dashboard',
        description: 'Advanced analytics tracking revenue, staff performance, customer satisfaction, and business growth metrics.',
        metrics: 'Real-time insights from 100+ KPIs',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile-First Experience',
        description: 'Optimized mobile platform for staff and customers with booking, payments, and communication tools.',
        metrics: '4.8/5 mobile satisfaction rating',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Package,
        title: 'Integrated Inventory Management',
        description: 'Smart inventory tracking with automated reordering, product recommendations, and retail sales optimization.',
        metrics: '70% reduction in inventory costs',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Business Intelligence',
        description: 'Machine learning algorithms providing predictive analytics, trend forecasting, and optimization recommendations.',
        metrics: '40% improvement in revenue prediction',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Marketing Automation Engine',
        description: 'Intelligent marketing campaigns with customer segmentation, automated follow-ups, and retention strategies.',
        metrics: '300% increase in marketing ROI',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Settings,
        title: 'Custom Workflow Builder',
        description: 'Flexible workflow automation enabling businesses to create custom processes and operational procedures.',
        metrics: 'Unlimited custom workflows',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Management',
        description: 'Centralized management for multiple beauty business locations with unified reporting and brand consistency.',
        metrics: 'Manage unlimited locations',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: CreditCard,
        title: 'Advanced Payment Processing',
        description: 'Comprehensive payment solutions with multiple payment methods, subscription management, and financial reporting.',
        metrics: '99.9% payment processing reliability',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Activity,
        title: 'Performance Analytics Suite',
        description: 'Detailed performance tracking for staff, services, products, and overall business health metrics.',
        metrics: 'Track 75+ performance indicators',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'booker-salon-platform': {
    keyBenefits: [
      {
        icon: Calendar,
        title: 'Advanced Salon Scheduling',
        description: 'Sophisticated appointment scheduling system with staff optimization, service coordination, and customer preferences.',
        metrics: '95% scheduling efficiency improvement',
        color: 'from-rose-500 to-pink-600'
      },
      {
        icon: Users,
        title: 'Customer Profile Management',
        description: 'Comprehensive customer database with service history, preferences, allergies, and personalized care notes.',
        metrics: '90% customer satisfaction increase',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: CreditCard,
        title: 'Integrated Payment Solutions',
        description: 'Seamless payment processing with multiple payment methods, tips, packages, and membership billing.',
        metrics: '99.9% payment success rate',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: TrendingUp,
        title: 'Marketing Campaign Tools',
        description: 'Advanced marketing automation with customer segmentation, promotional campaigns, and retention strategies.',
        metrics: '250% increase in marketing effectiveness',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: Activity,
        title: 'Staff Performance Analytics',
        description: 'Comprehensive staff performance tracking with productivity metrics, customer feedback, and growth insights.',
        metrics: '85% staff productivity improvement',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Star,
        title: 'Customer Loyalty Programs',
        description: 'Advanced loyalty and rewards system with point accumulation, tier benefits, and personalized offers.',
        metrics: '80% customer retention rate',
        color: 'from-teal-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Optimization',
        description: 'Machine learning algorithms optimizing scheduling, staff allocation, and resource utilization for maximum efficiency.',
        metrics: '60% operational efficiency gain',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Staff Management',
        description: 'Comprehensive mobile app for staff with scheduling, customer notes, payment processing, and communication tools.',
        metrics: '4.9/5 staff app satisfaction',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Package,
        title: 'Inventory Control System',
        description: 'Smart inventory management with automated reordering, cost tracking, and product performance analytics.',
        metrics: '70% inventory optimization',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Coordination',
        description: 'Centralized management for salon chains with unified customer profiles and cross-location booking.',
        metrics: 'Seamless multi-location experience',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Settings,
        title: 'Custom Service Configuration',
        description: 'Flexible service menu customisation with duration, pricing, staff requirements, and equipment needs.',
        metrics: 'Unlimited service customisation',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: BarChart3,
        title: 'Business Intelligence Suite',
        description: 'Advanced reporting and analytics with revenue forecasting, trend analysis, and performance optimization.',
        metrics: 'Real-time business insights',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'zenoti-enterprise-platform': {
    keyBenefits: [
      {
        icon: Building2,
        title: 'Enterprise-Grade Operations',
        description: 'Comprehensive enterprise solution for large spa and salon chains with advanced operational management.',
        metrics: '95% enterprise client satisfaction',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Users,
        title: 'Advanced Customer Experience',
        description: 'Sophisticated customer journey management with personalized experiences and premium service delivery.',
        metrics: '90% customer experience improvement',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Management',
        description: 'Centralized management for multiple locations with unified operations, reporting, and brand consistency.',
        metrics: 'Manage 100+ locations seamlessly',
        color: 'from-teal-500 to-blue-600'
      },
      {
        icon: BarChart3,
        title: 'Comprehensive Reporting Suite',
        description: 'Advanced business intelligence with detailed analytics, performance metrics, and strategic insights.',
        metrics: 'Track 200+ business KPIs',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Activity,
        title: 'Staff Productivity Analytics',
        description: 'Detailed staff performance tracking with productivity optimization and professional development insights.',
        metrics: '75% staff efficiency improvement',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Revenue Optimization Tools',
        description: 'Advanced revenue management with pricing optimization, package deals, and profit margin analysis.',
        metrics: '60% revenue increase average',
        color: 'from-amber-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Business Intelligence',
        description: 'Machine learning algorithms providing predictive analytics and automated business optimization recommendations.',
        metrics: 'Predictive accuracy of 92%',
        color: 'from-violet-500 to-indigo-600'
      },
      {
        icon: Settings,
        title: 'Custom Workflow Automation',
        description: 'Flexible automation platform enabling custom business processes and operational workflow optimization.',
        metrics: 'Unlimited workflow customisation',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Integration Ecosystem',
        description: 'Comprehensive integration capabilities with third-party systems, payment processors, and business tools.',
        metrics: 'Connect with 500+ systems',
        color: 'from-pink-500 to-purple-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Enterprise Platform',
        description: 'Complete mobile solution for enterprise operations with staff management, customer service, and reporting.',
        metrics: '4.8/5 enterprise mobile rating',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Advanced Customer Segmentation',
        description: 'Sophisticated customer analysis and segmentation for targeted marketing and personalized service delivery.',
        metrics: '85% segmentation accuracy',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: CreditCard,
        title: 'Enterprise Payment Solutions',
        description: 'Advanced payment processing with corporate billing, subscription management, and financial reporting.',
        metrics: '99.99% payment processing uptime',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  // AI & Personalization Products (7 products)
  'personal-stylist-platform': {
    keyBenefits: [
      {
        icon: Brain,
        title: 'AI Personal Styling Intelligence',
        description: 'Advanced AI algorithms analyzing body type, preferences, lifestyle, and trends to provide personalized styling recommendations.',
        metrics: '95% styling accuracy rate',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Eye,
        title: 'Virtual Wardrobe Management',
        description: 'Digital wardrobe cataloguing with outfit coordination, seasonal organisation, and purchase recommendations.',
        metrics: '80% wardrobe utilization improvement',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Camera,
        title: 'Visual Style Analysis',
        description: 'Computer vision technology analyzing photos to understand style preferences and suggest improvements.',
        metrics: '90% style preference accuracy',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Users,
        title: 'Professional Stylist Network',
        description: 'Access to certified personal stylists with booking, consultation, and ongoing style guidance.',
        metrics: '4.9/5 stylist satisfaction rating',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: ShoppingBag,
        title: 'Smart Shopping Assistant',
        description: 'AI-powered shopping recommendations with price tracking, deal alerts, and purchase optimization.',
        metrics: '60% reduction in shopping time',
        color: 'from-amber-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Style Trend Intelligence',
        description: 'Real-time fashion trend analysis with personalized trend adoption recommendations.',
        metrics: 'Track 1000+ fashion influencers',
        color: 'from-teal-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Zap,
        title: 'Real-Time Style Matching',
        description: 'Instant outfit coordination with AI-powered color matching and style compatibility analysis.',
        metrics: 'Sub-second style matching',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Scan,
        title: 'Body Type Analysis',
        description: 'Advanced body scanning and analysis for precise fit recommendations and flattering style suggestions.',
        metrics: '98% fit accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Style Platform',
        description: 'Comprehensive mobile app with outfit planning, shopping, and style tracking capabilities.',
        metrics: '4.8/5 mobile app rating',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Occasion-Based Styling',
        description: 'Context-aware styling for specific events, seasons, and occasions with weather integration.',
        metrics: 'Perfect styling for any event',
        color: 'from-pink-500 to-purple-600'
      },
      {
        icon: Settings,
        title: 'Customizable Style Profiles',
        description: 'Flexible style preference customisation with multiple profiles for different aspects of life.',
        metrics: 'Unlimited style profiles',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Global Fashion Integration',
        description: 'International fashion retailer integration with local availability and cultural style adaptation.',
        metrics: 'Connect with 500+ fashion retailers',
        color: 'from-violet-500 to-indigo-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'haut-ai-skin-analysis': {
    keyBenefits: [
      {
        icon: Scan,
        title: 'Advanced Skin Analysis AI',
        description: 'Cutting-edge computer vision technology analyzing skin conditions, aging signs, and treatment recommendations.',
        metrics: '96% skin analysis accuracy',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: Microscope,
        title: 'Dermatological Assessment',
        description: 'Professional-grade skin assessment with condition identification, severity scoring, and treatment planning.',
        metrics: 'Dermatologist-level accuracy',
        color: 'from-blue-500 to-purple-600'
      },
      {
        icon: Brain,
        title: 'Personalized Skincare Recommendations',
        description: 'AI-powered product recommendations based on skin analysis, concerns, and individual skin chemistry.',
        metrics: '90% recommendation effectiveness',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: TrendingUp,
        title: 'Skin Progress Tracking',
        description: 'Longitudinal skin health monitoring with progress visualization and treatment effectiveness measurement.',
        metrics: '85% improvement in skin health tracking',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Targeted Treatment Plans',
        description: 'Customized skincare regimens with product selection, application schedules, and progress milestones.',
        metrics: '80% treatment success rate',
        color: 'from-rose-500 to-red-600'
      },
      {
        icon: Eye,
        title: 'Real-Time Skin Monitoring',
        description: 'Continuous skin health assessment with environmental factor correlation and adaptive recommendations.',
        metrics: 'Daily skin health insights',
        color: 'from-amber-500 to-orange-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Camera,
        title: 'Multi-Spectral Imaging Analysis',
        description: 'Advanced imaging technology capturing visible and non-visible spectrum data for comprehensive skin analysis.',
        metrics: 'Professional imaging quality',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Zap,
        title: 'Instant Analysis Processing',
        description: 'Real-time skin analysis with immediate results and actionable recommendations.',
        metrics: 'Results in under 5 seconds',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Skin Database',
        description: 'Extensive database of skin conditions, treatments, and outcomes for accurate analysis and recommendations.',
        metrics: 'Database of 1M+ skin profiles',
        color: 'from-indigo-500 to-blue-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Skin Analysis',
        description: 'High-quality skin analysis using smartphone cameras with professional-grade accuracy.',
        metrics: '4.9/5 mobile analysis accuracy',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Users,
        title: 'Professional Integration',
        description: 'Seamless integration with dermatologists and skincare professionals for expert consultations.',
        metrics: 'Network of 1000+ skin experts',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: Settings,
        title: 'Custom Analysis Parameters',
        description: 'Flexible analysis configuration for different skin types, conditions, and treatment goals.',
        metrics: 'Unlimited analysis customisation',
        color: 'from-cyan-500 to-blue-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  }
};